import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext.jsx';
import { authenticatedFetch } from '../services/apiClient.js';
import FoxMark from '../components/shared/FoxMark.jsx';
import ReactiveFoxMark from '../components/shared/ReactiveFoxMark.jsx';
import NavMenu from '../components/shared/NavMenu.jsx';

const PAPER = '#F2EBDC';
const INK = '#1B1814';
const GOLD = '#A67C3A';

const TYPE_DESCRIPTIONS = {
  1: {
    name: 'The Reformer',
    tagline:
      'The one who believes things could always be better — and feels personally responsible to make them so.',
    core: 'You are wired for integrity. Somewhere deep in you lives a standard — not just for yourself, but for the world — and you feel the gap between what is and what ought to be more acutely than most people ever will. This is not perfectionism for its own sake. It is a genuine, aching love for goodness. You want to do right. You want to live right. And you want the people and systems around you to do the same. The burden you carry is that the inner critic never fully goes quiet. It is the voice that grades everything — your work, your words, your motives — against a standard that keeps moving. Your growth is not about lowering the standard. It is about learning that grace is not a compromise. It is the only thing that makes the standard survivable.',
    gift: 'You see what is broken and you have the discipline and courage to do something about it. You do not look away. You do not make excuses. When something needs to be fixed — a system, a relationship, an injustice — you are the one who will stay at it long after everyone else has moved on. Your honesty is a gift, even when it costs you.',
    shadow:
      'The inner critic does not only point outward. It points at you first. And when it cannot find fault in others, it finds it in yourself. The resentment that builds when people do not live up to your standards — including when you do not live up to your own — is the wound beneath the wound. The fear underneath it all is not that things will be imperfect. It is that you are imperfect. And that if people really saw that, they would not find you worthy.',
    growth:
      'Your growth edge is joy. Not happiness — joy. The ability to rest in what is good right now, without needing it to be better first. The 1 who is thriving has learned to laugh at their own mistakes. To let good enough be enough sometimes. To receive grace not as a lowering of the bar, but as the only honest response to being human.',
    scripture_theme:
      'Grace, rest, and the freedom that comes from being loved not for your performance but for who you are.',
  },
  2: {
    name: 'The Helper',
    tagline:
      'The one who knows what everyone needs — and quietly keeps track of whether it is ever returned.',
    core: 'You are wired for love. Not in a soft or sentimental way — in a deep, attentive, fiercely loyal way. You notice people. You remember what they said three months ago about something that mattered. You show up. You give generously and often at cost to yourself, because the alternative — being someone who does not help, does not care, is not needed — is more frightening than the exhaustion of giving too much. The complexity of your type is that the love is real and the need is real at the same time. You genuinely want to help. And you also genuinely need to matter. Both are true. The growth is not in choosing one over the other — it is in being honest about both.',
    gift: 'You make people feel seen in a world that mostly looks through them. You are the one who notices the quiet person in the room, who remembers the birthday, who asks the follow-up question. Your warmth is not performance — it is presence. And presence, real presence, is one of the rarest things one human being can offer another.',
    shadow:
      'There is a hidden ledger. You do not like that it exists, but it does. You track what you have given and you feel it — sometimes as a slow burn, sometimes as a sudden eruption — when it goes unacknowledged. The deeper fear is not that people will stop thanking you. It is that if you stopped giving, they would stop caring about you at all. That you are only as lovable as you are useful.',
    growth:
      'Your growth edge is receiving. Not giving better — receiving. Letting someone take care of you without deflecting or minimizing. Asking for what you need before you reach the point of resentment. The 2 who is thriving has learned that their needs are not a burden. That being loved for who they are — not just what they do — is possible. And that the people worth keeping in your life will prove it.',
    scripture_theme:
      'Being loved, not for what you do but for who you are. Rest, receiving, and the radical idea that you are enough without earning it.',
  },
  3: {
    name: 'The Achiever',
    tagline:
      'The one who can become whatever the room needs — and sometimes forgets who they actually are.',
    core: 'You are wired for excellence. You move fast, read rooms well, and know instinctively what success looks like in any given context. You deliver. You adapt. And you make it look effortless even when it is not. The shadow side of this gift is that the adapting never fully stops. You have been performing for so long — in your career, your relationships, your self-presentation — that the question of who you actually are beneath the performance can feel genuinely frightening. Not because there is nothing there. But because you have not had much practice just being it, without the audience.',
    gift: 'You inspire people. Not by telling them what to do — by showing them what is possible. Your energy, your drive, your ability to take a goal and actually reach it — these are rare. People follow you not because you demand it but because you move and they want to move with you. At your best you make the people around you believe they can do more than they thought.',
    shadow:
      'The fear underneath the achievement is that if you stopped achieving — stopped producing, stopped performing, stopped succeeding — there would be nothing left worth loving. That the real you, without the results, would not be enough. So the pace never really slows. Because slowing down means having to sit with that question. And the answer feels too uncertain to risk.',
    growth:
      'Your growth edge is stillness. Not inactivity — stillness. The ability to sit in a moment without needing it to produce something. To be known by someone who has seen your failures and stayed. The 3 who is thriving has learned that they are not their résumé. That the love worth having is the kind that does not require a performance.',
    scripture_theme:
      'Identity rooted in who you are, not what you accomplish. The rest that does not have to be earned. Being known and loved in the ordinary moments.',
  },
  4: {
    name: 'The Individualist',
    tagline:
      'The one who feels everything deeply — and sometimes mistakes the feeling for the truth.',
    core: 'You are wired for depth and authenticity. You feel things other people walk past. You notice beauty, tragedy, and meaning in places that most people do not even slow down for. You have a rich inner world — complex, nuanced, and often lonely — because the people around you can rarely go where you go emotionally. The longing is the center of your experience: the sense that something essential is always slightly out of reach. That others have something you do not. That you are fundamentally different — sometimes in a way that feels like a gift, sometimes like a wound.',
    gift: 'You tell the truth about what it means to be human. Your emotional honesty, your willingness to go to the dark places without flinching, your insistence on authenticity — these are gifts that the world desperately needs and rarely knows how to ask for. You create things — art, writing, conversations, relationships — that make people feel less alone in their own depths.',
    shadow:
      'The melancholy can become a home. The sense of being different, of being misunderstood, of having something missing — these feelings are real, but they can become an identity. And the identity can become a trap. The risk is that you romanticize the longing more than you pursue the belonging. That you keep yourself slightly apart because belonging feels like it would require giving up the very depth that makes you who you are.',
    growth:
      'Your growth edge is showing up — consistently, ordinarily, even when it does not feel profound. The 4 who is thriving has learned that meaning is not only found in the extraordinary moments. It is built in the daily, unglamorous act of being present. That belonging does not require you to be less yourself. That you can be deeply known and still be fully you.',
    scripture_theme:
      'You are known. Fully known — the depth, the darkness, the longing — and loved anyway. The God who meets you in the ordinary places, not only the transcendent ones.',
  },
  5: {
    name: 'The Investigator',
    tagline:
      'The one who understands almost everything — and sometimes keeps people at the distance required to do so.',
    core: 'You are wired for knowledge and self-sufficiency. You observe carefully before engaging. You think deeply before speaking. You conserve your energy because the world — with its demands, its emotions, its intrusions — can feel genuinely depleting in a way that is hard to explain to people who do not experience it the same way. Your mind is your sanctuary. And your competence is the armor that keeps the world from asking too much of you. The fear underneath is not of people — it is of being overwhelmed by them. Of being pulled into something you cannot exit. Of needing something and not having enough of yourself left to meet it.',
    gift: 'You see what others miss. You ask the question no one else thought to ask. You bring precision, depth, and a rare intellectual courage — the willingness to follow an idea wherever it leads, even if the destination is uncomfortable. The world needs people who think before they act. Who research before they conclude. Who bring rigor to a culture that mostly just reacts.',
    shadow:
      'The withdrawal protects you. But it also isolates you. And the isolation can begin to feel like safety when it is actually just distance. The knowledge accumulates and the connection does not. And at some point the competence that was supposed to make you safe from need starts to feel like the very thing keeping you from the life you were built for.',
    growth:
      'Your growth edge is engagement — before you are ready. Sharing the idea before it is fully formed. Staying in the room when it gets uncomfortable. Letting someone matter to you before you have figured out if they are worth the risk. The 5 who is thriving has learned that connection does not deplete the way they feared. That being known — really known — is not an intrusion. It is the thing they were longing for underneath all the research.',
    scripture_theme:
      'You do not have to earn your place at the table. You do not have to have all the answers before you belong. Come as you are — not as you will be when you have it all figured out.',
  },
  6: {
    name: 'The Loyalist',
    tagline:
      'The one who is always scanning for what could go wrong — and is usually right.',
    core: 'You are wired for loyalty and security. You think things through. You test people before you trust them, and once you trust them, you are faithful in a way that is increasingly rare. You are also the one in the room who has already thought about what happens if this goes wrong, and if that goes wrong, and what the backup plan is for the backup plan. This is not pessimism. It is vigilance. And the vigilance comes from a deep, often unspoken conviction that the world is not entirely safe — and that being caught unprepared is one of the worst things that can happen.',
    gift: 'You ask the questions other people are afraid to ask. You see the weak point in the plan before it becomes a crisis. You are the person others call when things actually fall apart, because you have already been thinking about it. Your loyalty is fierce and your commitment is real — and in a world full of people who disappear when things get hard, that is extraordinarily valuable.',
    shadow:
      'The anxiety scans for threat even when the threat has passed. And the scanning can become its own kind of prison — a constant low-level vigilance that makes it hard to rest, hard to trust fully, hard to believe that the good thing that is happening right now is not about to be taken away. The doubt can turn inward too — questioning your own judgment, second-guessing your own perceptions, looking for someone or something external to tell you what is real.',
    growth:
      'Your growth edge is trust — specifically, trusting yourself. The 6 who is thriving has learned that their own instincts are reliable. That they do not always need external authority to validate what they already know. That the courage they have been looking for outside of themselves was inside them the whole time. That faith is not the absence of doubt — it is moving anyway.',
    scripture_theme:
      'Do not be afraid. Not because nothing is uncertain — but because you are not alone in it. The anchor that does not shift when everything else does.',
  },
  7: {
    name: 'The Enthusiast',
    tagline:
      'The one who can find the possibility in anything — and sometimes uses possibility to avoid the present.',
    core: 'You are wired for joy and freedom. You move through the world with an energy and an openness to experience that most people spend years trying to cultivate and never quite get there. You are genuinely interested in ideas, people, experiences — you bring a contagious aliveness wherever you go. The shadow is that the movement is sometimes less about appetite and more about escape. The next thing, the next idea, the next experience — they feel like abundance. But sometimes they are avoidance. And what is being avoided is usually something painful that the 7 is not sure they can survive sitting with.',
    gift: 'You make the world more alive. You reframe what looks like a dead end into an unexpected opening. You bring energy into rooms that have gone flat. You are the person who says yes when everyone else is calculating the risk — and sometimes that yes changes everything. Your optimism is not naivety. At its best, it is a stubborn and creative insistence that there is always another way.',
    shadow:
      'The pain that got reframed eventually has to be felt. The commitment that got escaped eventually has to be made. The depth that got traded for breadth eventually has to be cultivated. The 7 can spend a lifetime collecting experiences without ever being fully changed by any of them — because being changed requires staying long enough to be uncomfortable.',
    growth:
      'Your growth edge is depth. Not more experiences — deeper ones. Staying in the conversation past the point where it gets interesting and into the point where it gets real. Letting a relationship develop scar tissue. Sitting with the grief or the disappointment or the ordinary long enough for it to actually do something in you. The 7 who is thriving has learned that the present moment — this one, exactly as it is — is enough.',
    scripture_theme:
      'Contentment. Not as a settling, but as a discovery — that the fullness you have been chasing has been available here the whole time. The joy that does not depend on the next thing.',
  },
  8: {
    name: 'The Challenger',
    tagline:
      'The one who walks into a room and immediately knows who is real and who is performing.',
    core: 'You are wired for strength and justice. You do not apologize for taking up space. You confront directly, protect fiercely, and lead instinctively. You have a finely tuned radar for weakness, dishonesty, and injustice — and very little patience for any of them. Beneath the intensity is a deep and genuine care for the people you have claimed as yours. The 8 does not fight for power. The 8 fights for the people who do not have enough of it. The wound underneath is around vulnerability. At some point early in your life you learned — or decided — that being vulnerable was dangerous. That softness invites harm. So you built the armor. And it worked. And now the armor is so well-fitted that sometimes you forget it is there.',
    gift: 'You make the people around you feel safe. Not comfortable — safe. There is a difference. Comfortable is what people feel when nothing is being asked of them. Safe is what people feel when they know that if something comes, someone will stand between them and it. You are that someone. And that is a rare and irreplaceable gift.',
    shadow:
      'The armor keeps out the pain. But it also keeps out the intimacy. The people who want to love you — really love you, not just admire or follow you — need access to the part of you that is uncertain. That is afraid. That does not have it all figured out. And the 8 often does not know how to give them that access without feeling like they are giving away the only thing that keeps them safe.',
    growth:
      'Your growth edge is tenderness. Not weakness — tenderness. The specific courage it takes to let someone see that you need them. To lead from love rather than from strength. The 8 who is thriving has learned that vulnerability does not make them less powerful. It makes them the kind of powerful that actually changes things — because people do not just follow them. They trust them.',
    scripture_theme:
      'Strength made perfect in weakness. The leader who washes feet. Power that does not grasp but gives itself away.',
  },
  9: {
    name: 'The Peacemaker',
    tagline:
      'The one who keeps the peace so well that sometimes no one — including themselves — knows what they actually want.',
    core: 'You are wired for harmony and connection. You have a remarkable ability to see all sides of a conflict, to hold space for people who feel unseen, to bring calm into rooms that have gone chaotic. You are easy to be around because you are genuinely interested in others and genuinely committed to their comfort. The cost of this gift is that your own wants, opinions, and needs tend to go underground. Not because you do not have them — but because surfacing them feels like a disruption. Like it might cost more than it is worth. And somewhere along the way you may have learned that the safest place is the one where you are needed but not demanding. Present but not insisting.',
    gift: 'You hold people. Not literally — but in the way that matters. You are the one who can sit with someone who is in a completely different place than you and make them feel genuinely met. You do not need people to agree with you or be like you. You are just there — curious, patient, non-anxious. In a world that is constantly demanding performance and agreement, that kind of presence is healing.',
    shadow:
      "The peace you keep is sometimes not peace at all. It is the suppression of conflict rather than the resolution of it. And suppression has a way of building — quietly, invisibly — until something gives. The 9 can spend years going along, agreeing, merging with others' priorities, and then wake up one day not entirely sure who they are apart from the people they have been accommodating.",
    growth:
      'Your growth edge is your own voice. Not volume — voice. The specific, particular, irreplaceable perspective that only you have, that you have been keeping quiet because you were not sure it was worth the friction. The 9 who is thriving has learned that their presence — their actual, distinct, sometimes inconvenient presence — is a gift. Not just to themselves. To the people who love them and have been waiting for them to show up.',
    scripture_theme:
      "You matter. Not as a background character in someone else's story — as a person with a name, a voice, a call. The God who comes looking for the one who wandered — not to bring them back to the group, but because they specifically are missed.",
  },
};

export default function ProfileOverview() {
  const navigate = useNavigate();
  const { userProfile, study, guide } = useApp();
  const [studyReady, setStudyReady] = useState(false);

  const type = userProfile?.enneagram_type;
  const wing = userProfile?.enneagram_wing;
  const label = userProfile?.enneagram_label;
  const desc = TYPE_DESCRIPTIONS[type];

  useEffect(() => {
    if (study) setStudyReady(true);
  }, [study]);

  async function handleManageSubscription() {
    try {
      const response = await authenticatedFetch('/api/create-portal-session', {
        method: 'POST',
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        console.error('[Profile] Portal session creation failed:', errData);
        alert(
          'Could not open billing portal. Please try again or contact support.'
        );
        return;
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      console.error('[Profile] Portal session error:', err);
      alert(
        'Could not open billing portal. Please try again or contact support.'
      );
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: PAPER }}>
      <div
        style={{
          padding: '20px 20px 0',
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
        }}
      >
        <FoxMark size={28} />
        <div style={{ textAlign: 'center' }}>
          <span
            style={{
              fontSize: 11,
              color: GOLD,
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              display: 'block',
            }}
          >
            Your Rooted Profile
          </span>
          <span
            style={{
              fontSize: 9,
              color: GOLD,
              fontFamily: 'Arial, sans-serif',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              opacity: 0.6,
              display: 'block',
              marginTop: 2,
            }}
          >
            by Golden Fox Publishing
          </span>
        </div>
        <div style={{ justifySelf: 'end' }}>
          <NavMenu />
        </div>
      </div>

      <div
        style={{
          maxWidth: 480,
          margin: '0 auto',
          padding: '32px 24px 120px',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          {type ? (
            <div
              style={{
                display: 'inline-block',
                background: GOLD,
                color: PAPER,
                borderRadius: 32,
                padding: '8px 20px',
                fontSize: 13,
                fontFamily: 'Arial, sans-serif',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: 12,
              }}
            >
              Type {type}
              {wing ? `w${wing}` : ''}
            </div>
          ) : null}
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: 36,
              fontWeight: 300,
              color: INK,
              margin: '0 0 8px',
            }}
          >
            {desc?.name || label || userProfile?.name || 'Your story'}
          </h1>
          <p
            style={{
              fontSize: 13,
              color: GOLD,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              margin: 0,
            }}
          >
            Prepared for {userProfile?.name || 'you'}
            {guide?.name ? ` · by ${guide.name}` : ''}
          </p>
        </div>

        <div
          style={{
            height: 1,
            background: `linear-gradient(to right, transparent, ${GOLD}44, transparent)`,
            margin: '0 0 32px',
          }}
        />

        {desc ? (
          <>
            <div style={{ marginBottom: 28 }}>
              <p
                style={{
                  fontSize: 11,
                  color: GOLD,
                  fontFamily: 'Arial, sans-serif',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: 10,
                }}
              >
                How you are wired
              </p>
              <p
                style={{
                  fontSize: 16,
                  color: INK,
                  lineHeight: 1.8,
                  fontFamily: 'Georgia, serif',
                  margin: 0,
                }}
              >
                {desc.core}
              </p>
            </div>

            <div
              style={{
                background: 'rgba(166,124,58,0.06)',
                border: `1px solid rgba(166,124,58,0.2)`,
                borderRadius: 12,
                padding: '16px 20px',
                marginBottom: 16,
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: GOLD,
                  fontFamily: 'Arial, sans-serif',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 8px',
                }}
              >
                Your gift
              </p>
              <p
                style={{
                  fontSize: 15,
                  color: INK,
                  lineHeight: 1.7,
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  margin: 0,
                }}
              >
                {desc.gift}
              </p>
            </div>

            <div
              style={{
                background: 'rgba(27,24,20,0.04)',
                border: `1px solid rgba(27,24,20,0.1)`,
                borderRadius: 12,
                padding: '16px 20px',
                marginBottom: 32,
              }}
            >
              <p
                style={{
                  fontSize: 11,
                  color: INK,
                  opacity: 0.4,
                  fontFamily: 'Arial, sans-serif',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  margin: '0 0 8px',
                }}
              >
                Your shadow
              </p>
              <p
                style={{
                  fontSize: 15,
                  color: INK,
                  opacity: 0.6,
                  lineHeight: 1.7,
                  fontFamily: 'Georgia, serif',
                  fontStyle: 'italic',
                  margin: 0,
                }}
              >
                {desc.shadow}
              </p>
            </div>

            {wing ? (
              <div style={{ marginBottom: 32 }}>
                <p
                  style={{
                    fontSize: 11,
                    color: GOLD,
                    fontFamily: 'Arial, sans-serif',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    marginBottom: 10,
                  }}
                >
                  Your wing — Type {wing}
                </p>
                <p
                  style={{
                    fontSize: 14,
                    color: INK,
                    opacity: 0.6,
                    lineHeight: 1.7,
                    fontFamily: 'Georgia, serif',
                    margin: 0,
                  }}
                >
                  Your {wing} wing adds a secondary flavor to how your type
                  expresses. It influences your communication style, your
                  relationships, and where you naturally put your energy.
                  Your guide has accounted for this in your study.
                </p>
              </div>
            ) : null}

            {desc.growth ? (
              <div style={{ marginBottom: 28 }}>
                <p
                  style={{
                    fontSize: 11,
                    color: GOLD,
                    fontFamily: 'Arial, sans-serif',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    marginBottom: 10,
                  }}
                >
                  Your growth edge
                </p>
                <p
                  style={{
                    fontSize: 15,
                    color: INK,
                    lineHeight: 1.8,
                    fontFamily: 'Georgia, serif',
                    margin: 0,
                  }}
                >
                  {desc.growth}
                </p>
              </div>
            ) : null}

            {desc.scripture_theme ? (
              <div
                style={{
                  background: 'rgba(166,124,58,0.06)',
                  border: `1px solid rgba(166,124,58,0.2)`,
                  borderRadius: 12,
                  padding: '16px 20px',
                  marginBottom: 32,
                }}
              >
                <p
                  style={{
                    fontSize: 11,
                    color: GOLD,
                    fontFamily: 'Arial, sans-serif',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    margin: '0 0 8px',
                  }}
                >
                  The theme of your study
                </p>
                <p
                  style={{
                    fontSize: 15,
                    color: INK,
                    lineHeight: 1.7,
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    margin: 0,
                  }}
                >
                  {desc.scripture_theme}
                </p>
              </div>
            ) : null}

            {desc.tagline ? (
              <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
                <p
                  style={{
                    fontSize: 14,
                    color: INK,
                    opacity: 0.4,
                    fontFamily: 'Georgia, serif',
                    fontStyle: 'italic',
                    margin: 0,
                    lineHeight: 1.6,
                  }}
                >
                  &ldquo;{desc.tagline}&rdquo;
                </p>
              </div>
            ) : null}
          </>
        ) : (
          <p
            style={{
              fontSize: 14,
              color: INK,
              opacity: 0.5,
              lineHeight: 1.7,
              fontFamily: 'Georgia, serif',
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: 32,
            }}
          >
            You skipped the Enneagram assessment — your study is still being
            built around everything you shared in your conversation.
          </p>
        )}

        {!studyReady && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '20px 0',
            }}
          >
            <ReactiveFoxMark
              amplitude={0}
              isActive={false}
              size={64}
              style={{ marginBottom: 24 }}
            />
            <div
              style={{
                display: 'flex',
                gap: 6,
                justifyContent: 'center',
                marginBottom: 8,
              }}
            >
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: GOLD,
                    animation: `typing-dot 1.2s ease-in-out ${i * 0.4}s infinite`,
                  }}
                />
              ))}
            </div>
            <p
              style={{
                fontSize: 13,
                color: INK,
                opacity: 0.4,
                fontFamily: 'Georgia, serif',
                fontStyle: 'italic',
                margin: 0,
              }}
            >
              {guide?.name || 'Your guide'} is building your first week...
            </p>
          </div>
        )}
      </div>

      {studyReady && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px 24px 32px',
            background: `linear-gradient(to top, ${PAPER} 70%, transparent)`,
          }}
        >
          <button
            type="button"
            onClick={handleManageSubscription}
            style={{
              width: '100%',
              maxWidth: 440,
              display: 'block',
              margin: '0 auto 12px',
              background: 'transparent',
              color: INK,
              border: `1px solid ${INK}`,
              borderRadius: 12,
              padding: '12px',
              fontSize: 14,
              cursor: 'pointer',
              fontWeight: 500,
              fontFamily: 'Georgia, serif',
            }}
          >
            Manage subscription
          </button>
          <button
            type="button"
            onClick={() => navigate('/study')}
            style={{
              width: '100%',
              maxWidth: 440,
              display: 'block',
              margin: '0 auto',
              background: INK,
              color: PAPER,
              border: 'none',
              borderRadius: 12,
              padding: '16px',
              fontSize: 16,
              cursor: 'pointer',
              fontWeight: 600,
              fontFamily: 'Georgia, serif',
            }}
          >
            Open my study →
          </button>
        </div>
      )}

      <style>{`
        @keyframes typing-dot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
